import { useEffect, useState } from 'react';
import { MessagePlugin, Table, Tag, type PrimaryTableCol } from 'tdesign-react';
import { Clock3, Eye, Globe2, MapPin, UserCircle } from 'lucide-react';
import { tenantAdminBusinessCardApi } from '../../api/index';

interface VisitorLog {
    cardTenantId : number
    cardUserId : number
    cardUserName : string
    createTime : string
    guestIpAddr : string
    guestUserId : string
    guestUserPhone : string
    id : number
    shareCode : string
    updateTime : string
}

const getPageData = (data: any) => ({
    list: Array.isArray(data) ? data : data?.list || data?.records || [],
    total: Array.isArray(data) ? data.length : data?.total || 0,
});

export default function eCardManageIndex() {
    const [data, setData] = useState<VisitorLog[]>([]);
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });

    const fetchData = async (pageNum = pagination.current, pageSize = pagination.pageSize) => {
        setLoading(true);
        try {
            const res = await tenantAdminBusinessCardApi.getVisitorLogPage({ pageNum, pageSize });
            if (res.code === 200) {
                const pageData = getPageData(res.data);
                setData(pageData.list);
                setPagination({ current: pageNum, pageSize, total: pageData.total });
            } else {
                MessagePlugin.error(res.message || '加载访客日志失败');
            }
        } catch {
            MessagePlugin.error('加载访客日志失败');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData(1, pagination.pageSize);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const columns: PrimaryTableCol<VisitorLog>[] = [
        {
            colKey: 'visitor',
            title: '访客',
            ellipsis: true,
            cell: ({ row }) => (
                <div className="flex items-center gap-2 min-w-0">
                    <UserCircle className="w-4 h-4 text-gray-400 shrink-0" />
                    <span className="truncate">{row.guestUserPhone || '匿名访客'}</span>
                </div>
            ),
        },
        {
            colKey: 'cardName',
            title: '访问名片',
            ellipsis: true,
            cell: ({ row }) => <span className="truncate block">{row.cardUserName || '-'}</span>,
        },
        {
            colKey: 'ip',
            title: 'IP 地址',
            ellipsis: true,
            cell: ({ row }) => (
                <div className="flex items-center gap-1 min-w-0">
                    <span className="truncate">{row.guestIpAddr || '-'}</span>
                </div>
            ),
        },
        {
            colKey: 'createTime',
            title: '访问时间',
            ellipsis: true,
            cell: ({ row }) => (
                <div className="flex items-center gap-1 text-gray-500 min-w-0">
                    <Clock3 className="w-3.5 h-3.5 shrink-0" />
                    <span className="truncate">{ row.createTime || '-'}</span>
                </div>
            ),
        }
    ];

    return (
        <div className="w-full min-w-0 max-w-full overflow-hidden">
            <div className="flex items-center gap-2 mb-4">
                <Eye className="w-5 h-5 text-[#1c5bff]" />
                <h1 className="text-lg font-semibold text-gray-900">访客记录</h1>
            </div>

            <div className="bg-white rounded-lg shadow-sm overflow-hidden min-w-0">
                <Table
                    rowKey="id"
                    loading={loading}
                    columns={columns}
                    data={data}
                    bordered={false}
                    hover
                    size="small"
                    tableLayout="fixed"
                    pagination={{
                        current: pagination.current,
                        pageSize: pagination.pageSize,
                        total: pagination.total,
                        showJumper: true,
                        showPageSize: true,
                        pageSizeOptions: [10, 20, 50],
                        onChange: ({ current, pageSize }) => fetchData(current, pageSize),
                    }}
                />
            </div>
        </div>
    );
}